import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-50 border-b-2 border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <a
            href="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0A0A0A] font-bold uppercase tracking-wider mb-6 transition-colors"
            style={{ fontFamily: "'Satoshi', sans-serif" }}
          >
            <ArrowLeft size={16} strokeWidth={3} />
            Back to Home
          </a>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0A0A0A]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="terms-heading"
          >
            Terms of Service
          </h1>
          <p className="text-gray-500 mt-3 font-medium" style={{ fontFamily: "'Satoshi', sans-serif" }}>
            Last updated: December 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16 space-y-10" style={{ fontFamily: "'Satoshi', sans-serif" }}>
        <Section title="1. Agreement to Terms">
          By accessing or using VelTech Ghana's cloud-based school management system ("the Service"), you agree to be bound by these Terms of Service. If you are entering into this agreement on behalf of a school or organization, you represent that you have the authority to bind that entity to these terms. If you do not agree to these terms, do not use the Service.
        </Section>

        <Section title="2. Description of Service">
          VelTech Ghana provides a cloud-based school management platform that includes student management, attendance tracking, exam and report card generation, fee and accounts management, timetabling, library management, HR and payroll, analytics and reporting, school website, and online admissions. The Service is provided on a subscription basis with custom pricing per school.
        </Section>

        <Section title="3. Account Registration and Access">
          <ul className="list-disc pl-6 space-y-2">
            <li>Schools must provide accurate and complete information during registration.</li>
            <li>The school administrator is responsible for managing user accounts and access permissions within their organization.</li>
            <li>You are responsible for maintaining the confidentiality of login credentials.</li>
            <li>You must notify us immediately of any unauthorized access to your account.</li>
            <li>VelTech Ghana reserves the right to suspend accounts that violate these terms.</li>
          </ul>
        </Section>

        <Section title="4. Subscription and Payment">
          <ul className="list-disc pl-6 space-y-2">
            <li>Pricing is custom for each school and outlined in your individual proposal agreement.</li>
            <li>Payment terms, billing cycles, and methods are specified in your proposal agreement.</li>
            <li>Failure to make timely payments may result in temporary suspension of access to the Service.</li>
            <li>All fees are quoted in Ghana Cedis (GH₵) unless otherwise stated.</li>
            <li>We reserve the right to adjust pricing with 30 days written notice.</li>
          </ul>
        </Section>

        <Section title="5. Data Ownership">
          <ul className="list-disc pl-6 space-y-2">
            <li>All school data entered into the system remains the property of the school.</li>
            <li>VelTech Ghana does not claim ownership of any student records, financial data, or academic information.</li>
            <li>Upon termination, schools may request a complete export of their data.</li>
            <li>VelTech Ghana retains the right to use anonymized, aggregated data for service improvement and analytics.</li>
          </ul>
        </Section>

        <Section title="6. Acceptable Use">
          <p className="mb-3">You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the Service for any unlawful purpose or in violation of Ghanaian law.</li>
            <li>Share login credentials with unauthorized individuals.</li>
            <li>Attempt to gain unauthorized access to other schools' data.</li>
            <li>Upload malicious software, viruses, or harmful code.</li>
            <li>Use the Service to store or transmit content that is defamatory, obscene, or otherwise objectionable.</li>
            <li>Reverse engineer, decompile, or attempt to extract the source code of the Service.</li>
            <li>Resell, sublicense, or redistribute access to the Service without written permission.</li>
          </ul>
        </Section>

        <Section title="7. Service Availability">
          VelTech Ghana strives to maintain 99.9% uptime. However, we do not guarantee uninterrupted access to the Service. Scheduled maintenance will be communicated in advance. We are not liable for downtime caused by factors beyond our reasonable control, including internet outages, power failures, or force majeure events.
        </Section>

        <Section title="8. On-Site Services">
          VelTech Ghana provides on-site setup, data migration, and staff training as part of our service. The scope of on-site services is defined in your individual proposal agreement. Additional on-site visits beyond the agreed scope may incur additional charges.
        </Section>

        <Section title="9. Intellectual Property">
          The Service, including its design, code, features, and documentation, is the intellectual property of VelTech Ghana. Your subscription grants you a non-exclusive, non-transferable license to use the Service for the duration of your subscription. This license does not include the right to modify, copy, or create derivative works of the Service.
        </Section>

        <Section title="10. Limitation of Liability">
          To the maximum extent permitted by Ghanaian law, VelTech Ghana shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, revenue, or business opportunities, arising from your use of the Service. Our total liability shall not exceed the amount paid by you for the Service during the 12 months preceding the claim.
        </Section>

        <Section title="11. Termination">
          <ul className="list-disc pl-6 space-y-2">
            <li>Either party may terminate the subscription with 30 days written notice.</li>
            <li>VelTech Ghana may terminate immediately if you breach these terms.</li>
            <li>Upon termination, we will provide a full data export within 14 business days.</li>
            <li>Data is retained for 90 days after termination, then permanently deleted.</li>
            <li>Fees paid in advance for unused periods may be refunded on a pro-rata basis at our discretion.</li>
          </ul>
        </Section>

        <Section title="12. Governing Law">
          These Terms of Service shall be governed by and construed in accordance with the laws of the Republic of Ghana. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Ghana.
        </Section>

        <Section title="13. Changes to Terms">
          VelTech Ghana reserves the right to modify these Terms of Service at any time. We will notify subscribed schools of material changes via email at least 30 days before they take effect. Continued use of the Service after changes take effect constitutes acceptance of the modified terms.
        </Section>

        <Section title="14. Contact Us">
          <p>For questions about these Terms of Service, contact us at:</p>
          <div className="mt-4 p-6 bg-gray-50 border-2 border-gray-100">
            <p><strong>VelTech Ghana</strong></p>
            <p>Email: info@veltechgh.com</p>
            <p>Phone: 0531 988 775</p>
            <p>WhatsApp: 0505 066 691</p>
            <p>Location: Accra, Ghana</p>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2
        className="text-xl font-black text-[#0A0A0A] mb-4 uppercase tracking-tight"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {title}
      </h2>
      <div className="text-gray-600 text-sm leading-relaxed font-medium" style={{ fontFamily: "'Satoshi', sans-serif" }}>
        {children}
      </div>
    </div>
  );
}
